import { Profile } from "@/hooks/profile/types";
import { useMemberSchedule } from "@/hooks/schedules/useMemberSchedule";
import { useScheduleChangeRequests } from "@/hooks/schedules/useScheduleChangeRequests";
import { useProfessorComments } from "@/hooks/schedules/useProfessorComments";
import { useAuth } from "@/contexts/AuthContext";
import { ScheduleCard } from "./ScheduleCard";
import { ChangeRequestForm } from "./ChangeRequestForm";
import { ChangeRequestList } from "./ChangeRequestList";
import { ProfessorCommentsList } from "./ProfessorCommentsList";
import { Loader2, Calendar, MessageSquare, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useMemo } from "react";
import { startOfWeek, endOfWeek, isWithinInterval } from "date-fns";

interface Props {
  profile: Profile;
}

export const SocioScheduleView = ({ profile }: Props) => {
  const { user } = useAuth();
  const { data: schedules, isLoading: schedulesLoading } = useMemberSchedule(profile.gym_id);
  const { requests, remainingChanges, createRequest, isLoading: requestsLoading } = useScheduleChangeRequests(profile.gym_id, "member");
  const { comments, isLoading: commentsLoading } = useProfessorComments(user?.id, profile.gym_id);

  const isLoading = schedulesLoading || requestsLoading || commentsLoading;

  // Find active approved changes for the current week per schedule
  const activeChangesMap = useMemo(() => {
    const map = new Map<string, typeof requests[0]>();
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

    requests
      .filter(r => r.status === "approved" && r.replacement_date)
      .forEach(r => {
        const [y, m, d] = r.replacement_date!.split("-").map(Number);
        const replDate = new Date(y, m - 1, d);
        if (isWithinInterval(replDate, { start: weekStart, end: weekEnd })) {
          map.set(r.schedule_id, r);
        }
      });
    return map;
  }, [requests]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!schedules || schedules.length === 0) {
    return (
      <div className="text-center py-12 space-y-3 animate-fade-in">
        <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50" />
        <p className="text-muted-foreground">No tenés turnos asignados todavía.</p>
        <p className="text-sm text-muted-foreground/70">Contactá al gimnasio para que te asignen un horario.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in px-1">
      {/* Schedule cards */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Mi turno asignado
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {schedules.map(schedule => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              activeChange={activeChangesMap.get(schedule.id) || null}
            />
          ))}
        </div>
      </section>

      {/* Professor comments */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          Comentarios de mi profesor
        </h2>
        <ProfessorCommentsList comments={comments} />
      </section>

      {/* Change request section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Solicitar cambio</h2>
          <Badge variant={remainingChanges > 0 ? "secondary" : "destructive"} className="text-xs">
            {remainingChanges > 0 ? (
              <>Te quedan {remainingChanges} cambios este mes</>
            ) : (
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Sin cambios disponibles
              </span>
            )}
          </Badge>
        </div>

        {remainingChanges > 0 && schedules.length > 0 && (
          <ChangeRequestForm
            schedules={schedules}
            gymId={profile.gym_id!}
            onSubmit={createRequest.mutate}
            isPending={createRequest.isPending}
          />
        )}

        {requests.length > 0 && <ChangeRequestList requests={requests} mode="member" />}
      </section>
    </div>
  );
};
